import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { db } from "@/firebase/config";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useToast } from "@/components/ui/use-toast";
import { Incident } from "./types";

interface InterventionDialogProps {
  schoolId: string;
  incident: Incident | null;
  onClose: () => void;
}

export const InterventionDialog = ({ schoolId, incident, onClose }: InterventionDialogProps) => {
  const { toast } = useToast();
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAssign = async () => {
    if (!incident || !type || !date) return;
    
    setLoading(true);
    try {
      await addDoc(collection(db, `schools/${schoolId}/disciplineInterventions`), {
        incidentId: incident.id,
        studentId: incident.studentId,
        studentName: incident.studentName,
        type,
        status: 'scheduled',
        startDate: new Date(date).toISOString(),
        description,
        createdAt: serverTimestamp()
      });

      toast({
        title: "Intervention Assigned",
        description: `${type} scheduled for ${incident.studentName}.`
      });
      onClose();
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to assign intervention.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!incident} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Intervention</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
            <div className="space-y-2">
                <Label>Student</Label>
                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded text-sm font-medium">
                    {incident?.studentName}
                </div>
            </div>

            <div className="space-y-2">
                <Label>Intervention Type</Label>
                <Select onValueChange={setType} value={type}>
                    <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="counseling">Counseling Session</SelectItem>
                        <SelectItem value="detention">Detention</SelectItem>
                        <SelectItem value="parent_conference">Parent Conference</SelectItem>
                        <SelectItem value="restorative_circle">Restorative Circle</SelectItem>
                        <SelectItem value="community_service">Community Service</SelectItem>
                        <SelectItem value="warning">Written Warning</SelectItem>
                        <SelectItem value="suspension">Suspension</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2">
                <Label>Schedule Date</Label>
                <Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} />
            </div>

            <div className="space-y-2">
                <Label>Notes / Instructions</Label>
                <Textarea 
                    placeholder="Specific instructions for the intervention..." 
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />
            </div>
        </div>

        <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleAssign} disabled={loading || !type || !date}>Assign Intervention</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
